import { create } from 'zustand'
import { teamsService } from '../services/teamsService'
import { Team, TeamMember, TeamInvitation, PersonalWorkspace, TeamSearchParams, TeamFilters, CreateTeamData } from '../types/teams'

interface TeamsStore {
  // Data
  teams: Team[]
  invitations: TeamInvitation[]
  personalWorkspace: PersonalWorkspace | null
  selectedTeam: Team | null
  
  // Loading states
  loading: {
    teams: boolean
    invitations: boolean
    personalWorkspace: boolean
    teamDetails: boolean
    members: boolean
    actions: boolean
  }
  
  // Error states
  error: string | null
  
  // UI State
  searchQuery: string
  activeFilters: string[]
  currentTab: 'my-teams' | 'discover' | 'invitations'
  selectedMember: TeamMember | null
  
  // Search & Filter
  searchParams: TeamSearchParams
  teamFilters: TeamFilters
  
  // Actions
  setCurrentTab: (tab: 'my-teams' | 'discover' | 'invitations') => void
  setSearchQuery: (query: string) => void
  addFilter: (filter: string) => void
  removeFilter: (filter: string) => void
  clearFilters: () => void
  
  // Data Actions
  refreshTeams: () => Promise<void>
  refreshInvitations: () => Promise<void>
  refreshPersonalWorkspace: () => Promise<void>
  loadTeamDetails: (teamId: string) => Promise<void>
  
  // Team Actions
  createTeam: (teamData: CreateTeamData) => Promise<void>
  updateTeam: (teamId: string, updates: Partial<Team>) => Promise<void>
  deleteTeam: (teamId: string) => Promise<void>
  leaveTeam: (teamId: string) => Promise<void>
  
  // Member Actions
  inviteMembers: (teamId: string, emails: string[], role?: string) => Promise<void>
  updateMemberRole: (teamId: string, memberId: string, role: string) => Promise<void>
  removeMember: (teamId: string, memberId: string) => Promise<void>
  
  // Invitation Actions
  acceptInvitation: (invitationId: string) => Promise<void>
  declineInvitation: (invitationId: string) => Promise<void>
  
  // Utility Actions
  clearError: () => void
  setError: (error: string) => void
  setLoading: (key: keyof TeamsStore['loading'], value: boolean) => void
}

export const useTeamsStore = create<TeamsStore>((set, get) => ({
  // Initial state
  teams: [],
  invitations: [],
  personalWorkspace: null,
  selectedTeam: null,
  
  loading: {
    teams: false,
    invitations: false,
    personalWorkspace: false,
    teamDetails: false,
    members: false,
    actions: false
  },
  
  error: null,
  
  searchQuery: '',
  activeFilters: [],
  currentTab: 'my-teams',
  selectedMember: null,
  
  searchParams: {
    query: '',
    filters: [],
    sortBy: 'name',
    sortOrder: 'asc',
    page: 1,
    limit: 20
  },
  
  teamFilters: {
    role: [],
    activity: [],
    size: [],
    type: []
  },
  
  // UI Actions
  setCurrentTab: (tab) => set({ currentTab: tab }),
  
  setSearchQuery: (query) => set({ 
    searchQuery: query,
    searchParams: { ...get().searchParams, query }
  }),
  
  addFilter: (filter) => set((state) => ({
    activeFilters: [...state.activeFilters, filter]
  })),
  
  removeFilter: (filter) => set((state) => ({
    activeFilters: state.activeFilters.filter(f => f !== filter)
  })),
  
  clearFilters: () => set({ 
    activeFilters: [],
    teamFilters: { role: [], activity: [], size: [], type: [] }
  }),
  
  // Data Actions
  refreshTeams: async () => {
    try {
      set((state) => ({ loading: { ...state.loading, teams: true }, error: null }))
      const teams = await teamsService.getUserTeams()
      set({ teams })
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load teams' })
    } finally {
      set((state) => ({ loading: { ...state.loading, teams: false } }))
    }
  },
  
  refreshInvitations: async () => {
    try {
      set((state) => ({ loading: { ...state.loading, invitations: true }, error: null }))
      const invitations = await teamsService.getInvitations()
      set({ invitations })
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load invitations' })
    } finally {
      set((state) => ({ loading: { ...state.loading, invitations: false } }))
    }
  },
  
  refreshPersonalWorkspace: async () => {
    try {
      set((state) => ({ loading: { ...state.loading, personalWorkspace: true }, error: null }))
      const personalWorkspace = await teamsService.getPersonalWorkspace()
      set({ personalWorkspace })
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load personal workspace' })
    } finally {
      set((state) => ({ loading: { ...state.loading, personalWorkspace: false } }))
    }
  },
  
  loadTeamDetails: async (teamId: string) => {
    try {
      set((state) => ({ loading: { ...state.loading, teamDetails: true }, error: null }))
      const team = await teamsService.getTeamDetails(teamId)
      set({ selectedTeam: team })
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load team details' })
    } finally {
      set((state) => ({ loading: { ...state.loading, teamDetails: false } }))
    }
  },
  
  // Team Actions
  createTeam: async (teamData: CreateTeamData) => {
    try {
      set((state) => ({ loading: { ...state.loading, actions: true }, error: null }))
      const newTeam = await teamsService.createTeam(teamData)
      set((state) => ({ teams: [...state.teams, newTeam] }))
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to create team' })
      throw error
    } finally {
      set((state) => ({ loading: { ...state.loading, actions: false } }))
    }
  },
  
  updateTeam: async (teamId: string, updates: Partial<Team>) => {
    try {
      set((state) => ({ loading: { ...state.loading, actions: true }, error: null }))
      const updatedTeam = await teamsService.updateTeam(teamId, updates)
      set((state) => ({
        teams: state.teams.map(team => 
          team.id === teamId ? { ...team, ...updatedTeam } : team
        ),
        selectedTeam: state.selectedTeam?.id === teamId 
          ? { ...state.selectedTeam, ...updatedTeam }
          : state.selectedTeam
      }))
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to update team' })
      throw error
    } finally {
      set((state) => ({ loading: { ...state.loading, actions: false } }))
    }
  },
  
  deleteTeam: async (teamId: string) => {
    try {
      set((state) => ({ loading: { ...state.loading, actions: true }, error: null }))
      await teamsService.deleteTeam(teamId)
      set((state) => ({
        teams: state.teams.filter(team => team.id !== teamId),
        selectedTeam: state.selectedTeam?.id === teamId ? null : state.selectedTeam
      }))
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to delete team' })
      throw error
    } finally {
      set((state) => ({ loading: { ...state.loading, actions: false } }))
    }
  },
  
  leaveTeam: async (teamId: string) => {
    try {
      set((state) => ({ loading: { ...state.loading, actions: true }, error: null }))
      await teamsService.leaveTeam(teamId)
      set((state) => ({
        teams: state.teams.filter(team => team.id !== teamId),
        selectedTeam: state.selectedTeam?.id === teamId ? null : state.selectedTeam
      }))
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to leave team' })
      throw error
    } finally {
      set((state) => ({ loading: { ...state.loading, actions: false } }))
    }
  },
  
  // Member Actions
  inviteMembers: async (teamId: string, emails: string[], role = 'member') => {
    try {
      set((state) => ({ loading: { ...state.loading, actions: true }, error: null }))
      await teamsService.inviteMembers(teamId, emails, role)
      // Refresh team details to get updated member list
      await get().loadTeamDetails(teamId)
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to invite members' })
      throw error
    } finally {
      set((state) => ({ loading: { ...state.loading, actions: false } }))
    }
  },
  
  updateMemberRole: async (teamId: string, memberId: string, role: string) => {
    try {
      set((state) => ({ loading: { ...state.loading, actions: true }, error: null }))
      await teamsService.updateMemberRole(teamId, memberId, role)
      // Refresh team details to get updated member list
      await get().loadTeamDetails(teamId)
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to update member role' })
      throw error
    } finally {
      set((state) => ({ loading: { ...state.loading, actions: false } }))
    }
  },
  
  removeMember: async (teamId: string, memberId: string) => {
    try {
      set((state) => ({ loading: { ...state.loading, actions: true }, error: null }))
      await teamsService.removeMember(teamId, memberId)
      // Refresh team details to get updated member list
      await get().loadTeamDetails(teamId)
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to remove member' })
      throw error
    } finally {
      set((state) => ({ loading: { ...state.loading, actions: false } }))
    }
  },
  
  // Invitation Actions
  acceptInvitation: async (invitationId: string) => {
    try {
      set((state) => ({ loading: { ...state.loading, actions: true }, error: null }))
      await teamsService.acceptInvitation(invitationId)
      await get().refreshInvitations()
      await get().refreshTeams()
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to accept invitation' })
      throw error
    } finally {
      set((state) => ({ loading: { ...state.loading, actions: false } }))
    }
  },
  
  declineInvitation: async (invitationId: string) => {
    try {
      set((state) => ({ loading: { ...state.loading, actions: true }, error: null }))
      await teamsService.declineInvitation(invitationId)
      await get().refreshInvitations()
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to decline invitation' })
      throw error
    } finally {
      set((state) => ({ loading: { ...state.loading, actions: false } }))
    }
  },
  
  // Utility Actions
  clearError: () => set({ error: null }),
  
  setError: (error: string) => set({ error }),
  
  setLoading: (key, value) => set((state) => ({
    loading: { ...state.loading, [key]: value }
  }))
})) 